import { createFileRoute } from '@tanstack/react-router'
import { Fragment, createElement, useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  Info,
  Loader2,
  Lock,
  MoreHorizontal,
  Plus,
} from 'lucide-react'
import { StatusFormDialog } from '../components/StatusFormDialog'
import {
  
  
  
  StatusRow
} from '../components/StatusRow'
import type { StatusDocument } from '@/lib/db/rxdb/schemas/status'
import type { ProgressType } from '@/lib/types/subscription'
import type {StatusColumn, StatusDropIndicator, StatusReorderEvent} from '../components/StatusRow';
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { usePageLayout } from '@/hooks/usePageLayout'
import SettingsSidebar from '@/components/app/sidebars/settings'
import { SettingsPageContainer, SettingsPageHeader } from '@/components/app/layout'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { useProjects, useStatusMutations } from '@/lib/db/rxdb/hooks'
import { useHasPermission } from '@/lib/hooks/useWorkspaceRole'
import { WORKSPACE_PERMISSIONS } from '@/lib/types/roles'
import { PROGRESS_TYPES, PROGRESS_TYPE_ORDER } from '@/lib/types/subscription'
import useLoginStore from '@/store/LoginStore'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'
import { compareRanks, generateRankBetween } from '@/lib/utils/lexorank'
import { rebalanceSortOrders } from '@/lib/utils/sortOrder'

const UNKNOWN_GROUP_KEY = '__unknown__'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/statuses',
)({
  component: StatusSettingsPage,
})

export default function StatusSettingsPage() {
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  const { t } = useTranslation('settings')
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const canManage = useHasPermission(
    WORKSPACE_PERMISSIONS.MANAGE_WORKSPACE_SETTINGS,
  )

  const [search, setSearch] = useState('')
  const statusHook = useStatuses()
  const statuses = statusHook.all.statuses
  const isLoading = statusHook.isLoading

  const { projects } = useProjects()
  const projectNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const project of projects) {
      map.set(project.id, project.name)
    }
    return map
  }, [projects])

  const { deleteStatus, updateStatus } = useStatusMutations()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [editingStatus, setEditingStatus] = useState<StatusDocument | undefined>(
    undefined,
  )

  const [deleteTarget, setDeleteTarget] = useState<StatusDocument | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const PROGRESS_TYPE_LABELS: Record<ProgressType, string> = useMemo(
    () => ({
      [PROGRESS_TYPES.BACKLOG]: t('status_management.progress_types.backlog'),
      [PROGRESS_TYPES.PLANNED]: t('status_management.progress_types.planned'),
      [PROGRESS_TYPES.IN_PROGRESS]: t(
        'status_management.progress_types.in_progress',
      ),
      [PROGRESS_TYPES.COMPLETED]: t(
        'status_management.progress_types.completed',
      ),
      [PROGRESS_TYPES.CANCELLED]: t(
        'status_management.progress_types.cancelled',
      ),
    }),
    [t],
  )

  const normalizeText = (value?: string | null) => value?.toLowerCase() ?? ''

  const searchTerm = normalizeText(search.trim())
  const isSearching = Boolean(searchTerm)

  const filteredStatuses = useMemo(() => {
    if (!isSearching) {
      return statuses
    }

    return statuses.filter((status) => {
      const matchesName = normalizeText(status.name).includes(searchTerm)
      const matchesDescription = normalizeText(status.description ?? '').includes(
        searchTerm,
      )
      return matchesName || matchesDescription
    })
  }, [isSearching, searchTerm, statuses])

  const openCreateDialog = () => {
    setDialogMode('create')
    setEditingStatus(undefined)
    setDialogOpen(true)
  }

  const openEditDialog = (status: StatusDocument) => {
    setDialogMode('edit')
    setEditingStatus(status)
    setDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await deleteStatus(deleteTarget.id)
      toast.success(t('status_management.toast.deleted'))
      setDeleteTarget(null)
    } catch (error) {
      logger.logError(error as Error, LogCategory.API_ERROR, {
        component: 'StatusSettingsPage',
        action: 'delete_status',
      })
      toast.error(t('status_management.toast.delete_failed'))
    } finally {
      setIsDeleting(false)
    }
  }

  const columns = useMemo<Array<StatusColumn>>(() => {
    const cols: Array<StatusColumn> = [
      {
        id: 'name',
        cell: (status) => (
          <div className="flex items-center gap-2">
            <StatusIcon
              progressType={status.progressType}
              statusId={status.id}
              size="sm"
            />
            <span className="font-medium text-sm text-foreground">
              {status.name}
            </span>
            {status.isSystemGenerated && (
              <Lock className="size-3 text-muted-foreground" />
            )}
          </div>
        ),
      },
      {
        id: 'progressType',
        cell: (status) => {
          const progressType = status.progressType as ProgressType
          return (
            <Badge variant="outline">
              {PROGRESS_TYPE_LABELS[progressType] || progressType}
            </Badge>
          )
        },
      },
      {
        id: 'scope',
        cell: (status) => {
          const count = status.projectIds?.length ?? 0
          if (status.isSystemGenerated) {
            return (
              <Badge variant="secondary" className="cursor-default font-normal">
                {t('status_management.scope.all_projects_system')}
              </Badge>
            )
          }
          if (count === 0) {
            return (
              <Badge
                variant="secondary"
                className="cursor-default font-normal"
              >
                {t('status_management.scope.all_projects')}
              </Badge>
            )
          }
          const names = status.projectIds
            .map((id) => projectNameById.get(id))
            .filter((n): n is string => Boolean(n))
          return (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="cursor-help font-normal">
                    {t('status_management.scope.n_projects', { count })}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-xs space-y-1">
                    {names.length > 0 ? (
                      names.map((name) => (
                        <div key={name} className="text-xs">
                          {name}
                        </div>
                      ))
                    ) : (
                      <div className="text-xs">
                        {t('status_management.scope.no_resolved')}
                      </div>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        },
      },
      {
        id: 'description',
        cell: (status) =>
          status.description ? (
            <span className="text-sm text-muted-foreground">
              {status.description}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground/60">
              {t('status_management.no_description')}
            </span>
          ),
      },
      {
        id: 'createdOn',
        cell: (status) => <StatusCreatedCell ts={status.createdOn} />,
      },
    ]

    if (canManage) {
      cols.push({
        id: 'actions',
        className: 'w-12',
        cell: (status) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                aria-label={t('status_management.actions.menu_label')}
              >
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => openEditDialog(status)}>
                {t('status_management.actions.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={status.isSystemGenerated}
                onSelect={() => {
                  if (status.isSystemGenerated) return
                  setDeleteTarget(status)
                }}
                className="text-destructive focus:text-destructive"
              >
                {t('status_management.actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      })
    }

    return cols
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PROGRESS_TYPE_LABELS, projectNameById, canManage, t])

  const columnHeaders = useMemo<Record<string, string>>(
    () => ({
      name: t('status_management.columns.name'),
      progressType: t('status_management.columns.progress_type'),
      scope: t('status_management.columns.scope'),
      description: t('status_management.columns.description'),
      createdOn: t('status_management.columns.created'),
      actions: '',
    }),
    [t],
  )

  const handleReorder = useCallback(
    (event: StatusReorderEvent) => {
      const source = statuses.find((s) => s.id === event.sourceId)
      const target = statuses.find((s) => s.id === event.targetId)
      if (!source || !target) return
      if (source.progressType !== target.progressType) return

      // Use the unfiltered bucket for rank math so search-active reorders
      // still place the row correctly relative to hidden neighbors.
      const bucket = statuses.filter(
        (s) => s.progressType === source.progressType,
      )
      const sourceIndex = bucket.findIndex((s) => s.id === source.id)
      if (sourceIndex === -1) return
      const reordered = [...bucket]
      reordered.splice(sourceIndex, 1)

      const targetIndex = reordered.findIndex((s) => s.id === target.id)
      if (targetIndex === -1) return

      let insertIndex = targetIndex
      if (sourceIndex < targetIndex) insertIndex -= 1
      if (event.dropPosition === 'after') insertIndex += 1
      insertIndex = Math.max(0, Math.min(reordered.length, insertIndex))
      reordered.splice(insertIndex, 0, source)

      const hasChanged = reordered.some(
        (item, index) => item.id !== bucket[index]?.id,
      )
      if (!hasChanged) return

      const movedIndex = reordered.findIndex((s) => s.id === source.id)
      const beforeSort =
        movedIndex > 0 ? reordered[movedIndex - 1]?.sortOrder ?? null : null
      const afterSort =
        movedIndex < reordered.length - 1
          ? reordered[movedIndex + 1]?.sortOrder ?? null
          : null

      const shouldRebalance =
        beforeSort !== null &&
        afterSort !== null &&
        compareRanks(beforeSort, afterSort) >= 0

      if (shouldRebalance) {
        void rebalanceSortOrders(reordered, async (id, updates) => {
          await updateStatus(id, updates)
        })
        return
      }

      const nextSortOrder = generateRankBetween(beforeSort, afterSort)
      if (nextSortOrder === source.sortOrder) return

      try {
        void updateStatus(source.id, { sortOrder: nextSortOrder })
      } catch (error) {
        logger.logError(error as Error, LogCategory.API_ERROR, {
          component: 'StatusSettingsPage',
          action: 'reorder_status',
        })
      }
    },
    [statuses, updateStatus],
  )

  return (
    <SettingsPageContainer innerClassName="space-y-6">
      <SettingsPageHeader
        title={t('status_management.title')}
        action={
          canManage && (
            <Button onClick={openCreateDialog} className="gap-2">
              <Plus className="size-4" />
              {t('status_management.new_button')}
            </Button>
          )
        }
      />

      {/* Info banner */}
      <Item variant="outline">
        <ItemMedia variant="icon">
          <Info className="size-5 text-blue-600 dark:text-blue-400" />
        </ItemMedia>
        <ItemContent>
          <ItemTitle>{t('status_management.banner.title')}</ItemTitle>
          <ItemDescription>
            {t('status_management.banner.body')}
          </ItemDescription>
        </ItemContent>
      </Item>

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full max-w-xs">
          <Input
            placeholder={t('status_management.search_placeholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-9"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center rounded-md border bg-card">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <StatusDataTable
          columns={columns}
          columnHeaders={columnHeaders}
          data={filteredStatuses}
          canManage={canManage}
          isSearching={isSearching}
          progressTypeLabels={PROGRESS_TYPE_LABELS}
          onReorder={handleReorder}
        />
      )}

      {workspaceId && (
        <StatusFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          mode={dialogMode}
          initialStatus={editingStatus}
          workspaceId={workspaceId}
        />
      )}

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('status_management.delete_dialog.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('status_management.delete_dialog.description', {
                name: deleteTarget?.name ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t('status_management.actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void confirmDelete()
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('status_management.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsPageContainer>
  )
}

function StatusCreatedCell({ ts }: { ts: number | null | undefined }) {
  const { smartTime, dateTime: formatDateTime } = useDateTimeFormatters()
  if (!ts) {
    return <span className="text-sm text-muted-foreground">—</span>
  }
  const date = new Date(ts)
  return (
    <time
      className="text-sm text-muted-foreground"
      title={formatDateTime(date, { dateStyle: 'medium', timeStyle: 'short' })}
      dateTime={date.toISOString()}
    >
      {smartTime(date)}
    </time>
  )
}

interface StatusDataTableProps {
  columns: Array<StatusColumn>
  columnHeaders: Record<string, string>
  data: Array<StatusDocument>
  canManage: boolean
  isSearching: boolean
  progressTypeLabels: Record<ProgressType, string>
  onReorder: (event: StatusReorderEvent) => void
}

function StatusDataTable({
  columns,
  columnHeaders,
  data,
  canManage,
  isSearching,
  progressTypeLabels,
  onReorder,
}: StatusDataTableProps) {
  const { t } = useTranslation('settings')
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dropIndicator, setDropIndicator] =
    useState<StatusDropIndicator | null>(null)

  const groups = useMemo(() => {
    const known = PROGRESS_TYPE_ORDER.map((pt) => ({
      progressType: pt as string,
      label: progressTypeLabels[pt],
      statuses: data.filter((s) => s.progressType === pt),
    }))
    const unknown = data.filter(
      (s) =>
        !(PROGRESS_TYPE_ORDER as ReadonlyArray<string>).includes(
          s.progressType,
        ),
    )
    if (unknown.length > 0) {
      known.push({
        progressType: UNKNOWN_GROUP_KEY,
        label: t('status_management.progress_types.unknown'),
        statuses: unknown,
      })
    }
    return known
  }, [data, progressTypeLabels, t])

  const visibleGroups = isSearching
    ? groups.filter((g) => g.statuses.length > 0)
    : groups

  const totalColSpan = columns.length + 1
  const allEmpty = visibleGroups.every((g) => g.statuses.length === 0)

  return (
    <div className="rounded-md border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            {columns.map((col) => (
              <TableHead key={col.id} className={col.className}>
                {columnHeaders[col.id] ?? ''}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {allEmpty ? (
            <TableRow>
              <TableCell colSpan={totalColSpan} className="h-24 text-center">
                {t('status_management.empty')}
              </TableCell>
            </TableRow>
          ) : (
            visibleGroups.map((group) => (
              <Fragment key={group.progressType}>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableCell colSpan={totalColSpan} className="py-2">
                    <div className="flex items-center gap-2">
                      <StatusIcon progressType={group.progressType} size="sm" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {group.label}
                      </span>
                      <Badge
                        variant="outline"
                        className="ml-1 px-1.5 py-0 text-[10px] font-normal"
                      >
                        {group.statuses.length}
                      </Badge>
                    </div>
                  </TableCell>
                </TableRow>
                {group.statuses.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={totalColSpan}
                      className="h-12 text-center text-sm text-muted-foreground/70"
                    >
                      {t('status_management.empty_bucket', {
                        type: group.label,
                      })}
                    </TableCell>
                  </TableRow>
                ) : (
                  group.statuses.map((status) => (
                    <StatusRow
                      key={status.id}
                      status={status}
                      columns={columns}
                      canDrag={
                        canManage && group.progressType !== UNKNOWN_GROUP_KEY
                      }
                      draggingId={draggingId}
                      onDragStart={setDraggingId}
                      onDragEnd={() => setDraggingId(null)}
                      onReorder={onReorder}
                      dropIndicator={dropIndicator}
                      onDropIndicatorChange={setDropIndicator}
                    />
                  ))
                )}
              </Fragment>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
